import { useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { setToken, clearToken } from "../Redux/authSlice"; // adjust import

type AuthResponse = {
  exp: string; // expiry timestamp as ISO string
  name: string;
  user_id: string;
};

type RefreshResponse = {
  result: {
    data: {
      token: string;
      expiry: string;
      userId: string;
      username: string;
    };
  };
};

export const useAuth = (
  tokenParam?: string,
  RefreshTokenParam?: string,
) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.auth);

  useEffect(() => {
    let refreshTimer: NodeJS.Timeout;

    const initAuth = async () => {
      const token = tokenParam || auth.token;
      const refreshToken = RefreshTokenParam || auth.refreshToken;

      if (!token) {
        // dispatch(clearToken()); // placeholder
        return;
      }

      try {
        let expiry: string | undefined = auth.expiry;
        let userId: string | undefined = auth.userId;
        let username: string | undefined = auth.username;

        // --- Case: Token from params ---
        if (tokenParam) {
          const res = await axios.post<AuthResponse>(
            `${process.env.REACT_APP_AUTH_URL}authenticate/`,
            { token }
          );
          const { exp, name, user_id } = res.data;

          if (!user_id) {
            dispatch(clearToken());
            return;
          }

          expiry = exp;
          userId = user_id;
          username = name;

          dispatch(setToken({ token, refreshToken, expiry, userId, username }));
        }

        // --- Case: Token from Redux ---
        else if (auth.token) {
          if (!expiry) {
            // No expiry → authenticate
            const res = await axios.post<AuthResponse>(
              `${process.env.REACT_APP_AUTH_URL}authenticate/`,
              { token }
            );
            const { exp, name, user_id } = res.data;

            if (!user_id) {
              dispatch(clearToken());
              return;
            }

            expiry = exp;
            userId = user_id;
            username = name;

            dispatch(setToken({ token, refreshToken, expiry, userId, username }));
          } else {
            // Expiry exists → validate time only
            const expiryTime = new Date(expiry).getTime() * 1000;
            if (Date.now() >= expiryTime) {
              if (!refreshToken) {
                dispatch(clearToken());
                return;
              }
              // Expired → refresh
              const refreshRes = await axios.post<any>(
                `${process.env.REACT_APP_AUTH_URL}authenticate/refresh`,
                { refreshToken }
              );

              console.log("updating new")
              dispatch(
                setToken({
                  refreshToken: refreshRes.data.result.data.refreshToken,
                  token: refreshRes.data.result.data.accessToken,
                  expiry: refreshRes.data.result.data.expiresIn,
                  userId,
                  username,
                })
              );
              return;
            }
          }
        }

        // --- Setup refresh before expiry ---
        if (expiry) {
          const expiryTime = new Date(expiry).getTime() * 1000;
          const refreshAt = expiryTime - 4 * 60 * 1000; // 4 minutes before
          const delay = refreshAt - Date.now();

          if (delay > 0) {
            refreshTimer = setTimeout(async () => {
              try {
                const refreshRes = await axios.post<any>(
                `${process.env.REACT_APP_AUTH_URL}authenticate/refresh`,
                  { refreshToken }
                );
                // console.log(refreshRes.data.result.data.accessToken)
                // console.log(refreshRes.data.result.data.expiresIn)
                // console.log(refreshRes.data.result.data.refreshToken)
                console.log("updating new")
                dispatch(
                  setToken({
                    refreshToken: refreshRes.data.result.data.refreshToken,
                    token: refreshRes.data.result.data.accessToken,
                    expiry: refreshRes.data.result.data.expiresIn,
                    userId,
                    username,
                  })
                );
              } catch {
                console.error("Error refreshing token");
                dispatch(clearToken());
              }
            }, delay);
          }
        }
      } catch (err) {
        console.error("Auth error:", err);
        dispatch(clearToken());
      }
    };

    initAuth();

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [dispatch, tokenParam, RefreshTokenParam, auth.token, auth.expiry]);

  return auth
};
